import React, { createContext, useContext, useState, useEffect } from 'react';
import { vendorService } from '../services/vendorService';

const VendorContext = createContext();

export const useVendorContext = () => {
  const context = useContext(VendorContext);
  if (!context) {
    throw new Error('useVendorContext must be used within VendorProvider');
  }
  return context;
};

export const VendorProvider = ({ children }) => {
  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [messages, setMessages] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadVendors();
  }, []);

  const loadVendors = async () => {
    try {
      setLoading(true);
      const data = await vendorService.getVendors();
      setVendors(data);
    } catch (error) {
      console.error('Error loading vendors:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (vendorId) => {
    try {
      const data = await vendorService.getMessages(vendorId);
      setMessages(prev => ({ ...prev, [vendorId]: data }));
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  const addVendor = async (name) => {
    try {
      const newVendor = await vendorService.createVendor(name);
      setVendors([...vendors, newVendor]);
      return newVendor;
    } catch (error) {
      console.error('Error adding vendor:', error);
      throw error;
    }
  };

  const addMessage = async (vendorId, messageData) => {
    try {
      const newMessage = await vendorService.createMessage({
        vendorId,
        ...messageData
      });
      
      setMessages(prev => ({
        ...prev,
        [vendorId]: [...(prev[vendorId] || []), newMessage]
      }));

      // Update vendor last message
      setVendors(vendors.map(v =>
        v.id === vendorId ? { ...v, lastMessage: messageData.text } : v
      ));

      return newMessage;
    } catch (error) {
      console.error('Error adding message:', error);
      throw error;
    }
  };

  const selectVendor = (vendor) => {
    setSelectedVendor(vendor);
    if (!messages[vendor.id]) {
      loadMessages(vendor.id);
    }
  };

  const value = {
    vendors,
    selectedVendor,
    messages,
    loading,
    addVendor,
    addMessage,
    selectVendor,
    loadVendors
  };

  return (
    <VendorContext.Provider value={value}>
      {children}
    </VendorContext.Provider>
  );
};